import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { A11yModule } from '@angular/cdk/a11y';
import { AdminService, DiscoveryPage, DiscoveryPageListResponse } from '../../services/admin.service';

@Component({
  selector: 'app-discovery-pages-management',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatExpansionModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatSnackBarModule,
    MatTooltipModule,
    MatCardModule,
    A11yModule
  ],
  templateUrl: './discovery-pages-management.component.html',
  styleUrl: './discovery-pages-management.component.scss'
})
export class DiscoveryPagesManagementComponent implements OnInit {
  private adminService = inject(AdminService);
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);

  pages: DiscoveryPage[] = [];
  totalPages = 0;
  currentPage = 1;
  pageSize = 100;
  loading = false;

  ngOnInit(): void {
    this.loadPages();
  }

  loadPages(): void {
    this.loading = true;
    const skip = (this.currentPage - 1) * this.pageSize;
    
    this.adminService.getDiscoveryPages(skip, this.pageSize).subscribe({
      next: (response: DiscoveryPageListResponse) => {
        this.pages = response.pages;
        this.totalPages = Math.ceil(response.total / this.pageSize);
        this.loading = false;
      },
      error: (err) => {
        console.error('Failed to load discovery pages:', err);
        this.snackBar.open(
          err.error?.detail || err.error?.message || 'Failed to load discovery pages',
          'Close',
          { duration: 5000 }
        );
        this.loading = false;
      }
    });
  }

  deletePage(page: DiscoveryPage): void {
    // First confirmation dialog
    const firstDialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '450px',
      disableClose: true,
      data: { 
        domain: page.domain,
        step: 1,
        title: 'Delete Discovery Page?',
        message: 'Are you sure you want to delete this discovery page?',
        warning: 'This will delete all the scraped data used for different projects.',
        confirmText: 'Yes, Continue',
        cancelText: 'Cancel'
      }
    });

    firstDialogRef.afterClosed().subscribe(firstResult => {
      if (firstResult) {
        // Second confirmation dialog
        const secondDialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
          width: '450px',
          disableClose: true,
          data: { 
            domain: page.domain,
            step: 2,
            title: 'Final Confirmation',
            message: 'This action is irreversible.',
            warning: 'All scraped data for this domain will be permanently deleted and cannot be recovered.',
            confirmText: 'Yes, Delete Permanently',
            cancelText: 'Cancel'
          }
        });

        secondDialogRef.afterClosed().subscribe(secondResult => {
          if (secondResult) {
            this.adminService.deleteDiscoveryPage(page.id).subscribe({
              next: () => {
                this.snackBar.open('Discovery page deleted successfully', 'Close', { duration: 3000 });
                this.loadPages();
              },
              error: (err) => {
                this.snackBar.open(
                  err.error?.detail || err.error?.message || 'Failed to delete discovery page',
                  'Close',
                  { duration: 5000 }
                );
              }
            });
          }
        });
      }
    });
  }

  copyAllContent(page: DiscoveryPage): void {
    // Combine all pages_text content
    let contentToCopy = '';
    
    if (page.pages_text && Object.keys(page.pages_text).length > 0) {
      contentToCopy = Object.entries(page.pages_text)
        .map(([url, content]) => `URL: ${url}\n\n${content}\n\n${'='.repeat(80)}\n\n`)
        .join('');
    } else {
      contentToCopy = 'No content available';
    }

    navigator.clipboard.writeText(contentToCopy).then(() => {
      this.snackBar.open('All content copied to clipboard', 'Close', { duration: 2000 });
    }).catch(() => {
      this.snackBar.open('Failed to copy content', 'Close', { duration: 3000 });
    });
  }

  copyPageContent(page: DiscoveryPage, url: string): void {
    const content = page.pages_text?.[url] || 'No content available';
    navigator.clipboard.writeText(content).then(() => {
      this.snackBar.open('Page content copied to clipboard', 'Close', { duration: 2000 });
    }).catch(() => {
      this.snackBar.open('Failed to copy content', 'Close', { duration: 3000 });
    });
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadPages();
    }
  }

  getPagesTextCount(page: DiscoveryPage): number {
    return page.pages_text ? Object.keys(page.pages_text).length : 0;
  }

  getPagesTextPreview(page: DiscoveryPage): string {
    if (!page.pages_text || Object.keys(page.pages_text).length === 0) {
      return 'No content';
    }
    const firstUrl = Object.keys(page.pages_text)[0];
    const content = page.pages_text[firstUrl];
    if (!content) {
      return 'No content';
    }
    if (content.length <= 100) {
      return content;
    }
    return content.substring(0, 100) + '...';
  }

  getPagesUrls(page: DiscoveryPage): string[] {
    return page.pages || [];
  }

  hasPagesText(page: DiscoveryPage): boolean {
    return page.pages_text && Object.keys(page.pages_text).length > 0;
  }

  hasPageContent(page: DiscoveryPage, url: string): boolean {
    return page.pages_text && page.pages_text[url] ? true : false;
  }
}

// Simple confirmation dialog component
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Inject } from '@angular/core';

@Component({
  selector: 'app-confirm-delete-dialog',
  template: `
    <h2 mat-dialog-title>
      <mat-icon [class]="data.step === 2 ? 'warning-icon' : 'info-icon'">
        {{ data.step === 2 ? 'warning' : 'info' }}
      </mat-icon>
      {{ data.title }}
    </h2>
    <mat-dialog-content>
      <div class="dialog-content">
        <p class="main-message">{{ data.message }}</p>
        <div class="warning-box">
          <mat-icon>warning</mat-icon>
          <p class="warning-text">{{ data.warning }}</p>
        </div>
        <div class="domain-info">
          <strong>Domain:</strong> {{ data.domain }}
        </div>
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="onNoClick()">{{ data.cancelText }}</button>
      <button mat-raised-button color="warn" [mat-dialog-close]="true" cdkFocusInitial>
        {{ data.confirmText }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    h2[mat-dialog-title] {
      display: flex;
      align-items: center;
      gap: 12px;
      margin: 0;
      
      mat-icon {
        font-size: 28px;
        width: 28px;
        height: 28px;
        
        &.warning-icon {
          color: #f44336;
        }
        
        &.info-icon {
          color: #2196f3;
        }
      }
    }

    .dialog-content {
      padding: 8px 0;
    }

    .main-message {
      font-size: 16px;
      margin: 0 0 16px 0;
      color: #333;
    }

    .warning-box {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 16px;
      background-color: #fff3cd;
      border: 1px solid #ffc107;
      border-radius: 4px;
      margin-bottom: 16px;

      mat-icon {
        color: #f57c00;
        font-size: 24px;
        width: 24px;
        height: 24px;
        flex-shrink: 0;
        margin-top: 2px;
      }

      .warning-text {
        margin: 0;
        color: #856404;
        font-size: 14px;
        line-height: 1.5;
        font-weight: 500;
      }
    }

    .domain-info {
      padding: 12px;
      background-color: #f5f5f5;
      border-radius: 4px;
      font-size: 14px;
      color: #666;

      strong {
        color: #333;
        margin-right: 8px;
      }
    }

    mat-dialog-actions {
      margin-top: 24px;
      padding: 16px 0 0 0;
    }
  `],
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatDialogModule, MatIconModule, A11yModule]
})
export class ConfirmDeleteDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<ConfirmDeleteDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { 
      domain: string;
      step: number;
      title: string;
      message: string;
      warning: string;
      confirmText: string;
      cancelText: string;
    }
  ) {}

  onNoClick(): void {
    this.dialogRef.close();
  }
}
